/**
 * useUpdatePropertyStatus — TanStack mutation over PATCH
 * `/organizations/:slug/properties/:id` with a status-only body.
 *
 * The BE PATCH endpoint takes the full `UpdatePropertyDto` and an
 * `If-Match` header carrying the optimistic-concurrency etag (the
 * `version` field on `PropertyResponseDto`). We send a partial body
 * `{ status: { billing, visibility } }` and the BE merges. On
 * success, the mutation invalidates the property's detail key so the
 * UI refetches the fresh row (with the bumped `version`).
 *
 * The hard guard (Inactive + Bookable forbidden) is enforced on BOTH
 * sides:
 *   - Client: `UnitStatusModal` disables the Bookable radio when
 *     Inactive is selected; Save button disabled in forbidden state.
 *   - Server: PATCH returns 422 with a specific reason. Client never
 *     bypasses (per STATUS_MATRIX.md §3 + CLAUDE.md non-negotiable §4).
 *
 * The hook returns a status union so the modal can show inline error
 * states on 422 / 409 (optimistic-lock conflict — concurrent edit).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { propertyQueryKeys } from './queryKeys.js';
import type {
  UnitAvailability,
  UnitOperational,
} from './types.js';

export interface UpdatePropertyStatusVariables {
  readonly propertyId: string;
  /** Optimistic-concurrency token from the loaded `PropertyView.version`. */
  readonly version: number;
  readonly op: UnitOperational;
  readonly av: UnitAvailability;
}

/**
 * Translates bundle vocabulary (`op` / `av`, hyphenated visibility)
 * back to the BE's `UpdatePropertyStatusDto` shape (`billing` /
 * `visibility` with underscore enum). The inverse of `dto-mapper.ts`'s
 * `mapVisibility` — kept named so the round-trip contract is visible.
 */
function toBeStatusDto(input: {
  op: UnitOperational;
  av: UnitAvailability;
}): { billing: UnitOperational; visibility: 'bookable' | 'non_bookable' } {
  return {
    billing: input.op,
    visibility: input.av === 'non-bookable' ? 'non_bookable' : 'bookable',
  };
}

export function useUpdatePropertyStatus(): {
  readonly mutateAsync: (vars: UpdatePropertyStatusVariables) => Promise<void>;
  readonly status: 'idle' | 'pending' | 'success' | 'error';
  readonly errorKind: 'forbidden-state' | 'conflict' | 'other' | null;
  readonly reset: () => void;
} {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  const mutation = useMutation<
    undefined,
    Error,
    UpdatePropertyStatusVariables
  >({
    mutationFn: async (vars) => {
      if (orgSlug === null) {
        throw new Error('Cannot update property status without an org slug');
      }
      // Client-side hard guard — never let the modal short-circuit
      // submit when forbidden. The BE will also reject (422) but
      // catching here gives a cleaner UX (no round trip).
      if (vars.op === 'inactive' && vars.av === 'bookable') {
        const err = new Error('FORBIDDEN_STATE') as Error & {
          kind: 'forbidden-state';
        };
        err.kind = 'forbidden-state';
        throw err;
      }
      const result = await apiClient.PATCH(
        '/organizations/{slug}/properties/{id}',
        {
          params: {
            path: { slug: orgSlug, id: vars.propertyId },
            // Strong entity-tag per RFC 7232 — the BE etag parser
            // (apps/api/.../interface/utils/etag.ts) requires the quoted
            // form `"<version>"`; a bare `3` 400s before the use case.
            header: { 'If-Match': `"${vars.version}"` },
          },
          body: {
            status: toBeStatusDto({ op: vars.op, av: vars.av }),
          },
        },
      );
      if (result.response.status === 412 || result.response.status === 409) {
        const err = new Error('CONFLICT') as Error & { kind: 'conflict' };
        err.kind = 'conflict';
        throw err;
      }
      if (result.response.status === 422) {
        const err = new Error('FORBIDDEN_STATE') as Error & {
          kind: 'forbidden-state';
        };
        err.kind = 'forbidden-state';
        throw err;
      }
      if (!result.response.ok) {
        throw new Error(
          `PATCH /organizations/${orgSlug}/properties/${vars.propertyId} failed with status ${result.response.status}`,
        );
      }
      // Invalidate the property's detail + list so the page refetches.
      await queryClient.invalidateQueries({
        queryKey: propertyQueryKeys.detail(vars.propertyId),
      });
      await queryClient.invalidateQueries({
        queryKey: propertyQueryKeys.lists(),
      });
      return undefined;
    },
  });

  const errorKind = ((): 'forbidden-state' | 'conflict' | 'other' | null => {
    if (mutation.status !== 'error' || mutation.error === null) return null;
    const e = mutation.error as Error & {
      kind?: 'forbidden-state' | 'conflict';
    };
    if (e.kind === 'forbidden-state') return 'forbidden-state';
    if (e.kind === 'conflict') return 'conflict';
    return 'other';
  })();

  return {
    mutateAsync: async (vars) => {
      await mutation.mutateAsync(vars);
    },
    status: mutation.status,
    errorKind,
    reset: mutation.reset,
  };
}
